/**
 * Заявки (`/admin/clinic-applications`) — одна очередь на две двери:
 * заявка из приложения (телефон подтверждён кодом) и лид с лендинга
 * (имя и номер, больше ничего). Оркестратор: фильтры, таблица,
 * карточка-drawer, форма создания клиента, тост.
 *
 * Выбранную заявку держим по id, а не объектом: карточка умеет открыть
 * заявку, которой в текущем отфильтрованном списке нет (конфликт
 * открытого лида по тому же номеру). Строка из списка идёт снапшотом —
 * первым кадром, пока летит свежая карточка.
 *
 * Отсюда же заводится клиент. Раньше карточка просто советовала пойти
 * в «Клиенты» и создать аккаунт руками: данные заявки переписывались
 * глазами, а связи между заявкой и клиентом не оставалось вовсе.
 */

import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';

import {type ApplicationItem, getApplications} from '@services/applications';
import {keys} from '@queries/keys';
import {useToast} from '@hooks/useToast';
import {Toast} from '@components/Toast';
import {Loader} from '@components/ui/Loader';
import {phoneDigits} from '@utils/phone';
import {CreateAccountModal} from '@pages/Clients/CreateAccountModal';

import {ApplicationDrawer} from './ApplicationDrawer';
import {
  type SourceFilter,
  sourceToVerified,
  STATUS_LABEL,
  type StatusFilter,
} from './Applications.utils';
import {ApplicationsFilters} from './ApplicationsFilters';
import {ApplicationsTable} from './ApplicationsTable';

export const Applications = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSnap, setSelectedSnap] = useState<ApplicationItem | null>(null);
  /** Заявка, по которой открыта форма создания клиента */
  const [convertTarget, setConvertTarget] = useState<ApplicationItem | null>(null);
  const {toast, notify} = useToast();

  const req = {
    ...(filter !== 'all' && {status: filter}),
    ...(source !== 'all' && {phoneVerified: sourceToVerified(source)}),
  };
  const {data, isLoading} = useQuery({
    queryKey: keys.applications.list(req),
    queryFn: () => getApplications(req),
  });

  const rows = data?.list ?? [];
  const counts = data?.statusCounts ?? {};
  const total = (counts.pending ?? 0) + (counts.reviewed ?? 0) + (counts.rejected ?? 0);

  const handleOpen = (id: string) => {
    setSelectedSnap(rows.find((a) => a.id === id) ?? null);
    setSelectedId(id);
  };

  const handleClose = () => setSelectedId(null);

  const handleStatusChanged = (updated: ApplicationItem) => {
    setSelectedSnap(updated);
    notify(`Заявка: ${STATUS_LABEL[updated.status].toLowerCase()}`, 2200);
  };

  /* Дровер закрываем: форма создания — следующий шаг, а не дополнение к
     карточке, и модалка поверх дровера читается как «я что-то не закрыл».
     Заявку берём ту, что дровер показывал — он перечитывает её по ручке,
     и его копия свежее строки списка, из которой мы бы взяли снапшот. */
  const handleConvert = (application: ApplicationItem) => {
    setConvertTarget(application);
    setSelectedId(null);
  };

  const handleConvertDone = (open: boolean) => {
    if (!open) setConvertTarget(null);
  };

  /* Тост показываем отсюда: модалка смонтирована условно, и её
     собственный тост умирает вместе с размонтированием. */
  const handleCreated = () => notify('Клиент заведён · триал 30 дней', 2600);

  /* Отдельного маршрута у карточки клиента нет — она открывается из
     списка. Поэтому ведём в «Клиенты» с поиском по номеру владельца:
     поиск ищет и по телефону, номер уникален (по нему же идёт дедуп). */
  const handleOpenClientByPhone = (phone: string) => {
    navigate(`/clients?q=${encodeURIComponent(phoneDigits(phone))}`);
  };

  const chipCount = (key: StatusFilter): number => (key === 'all' ? total : (counts[key] ?? 0));

  return (
    <>
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur max-md:px-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold -tracking-[.02em]">Заявки</h1>
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            Клиники с сайта и из приложения · клиент заводится из карточки заявки
          </div>
        </div>
      </header>

      <div className="max-w-[1080px] px-[30px] pb-[60px] pt-[22px] max-md:px-4">
        <ApplicationsFilters
          status={filter}
          source={source}
          countOf={chipCount}
          onStatus={setFilter}
          onSource={setSource}
        />

        {isLoading ? (
          <div className="flex min-h-[440px] items-center justify-center">
            <Loader label="Загружаем заявки…" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex min-h-[300px] items-center justify-center text-[13.5px] text-muted-foreground">
            Заявок нет
          </div>
        ) : (
          <div className="overflow-hidden rounded-[13px] border border-skr-line bg-card">
            <ApplicationsTable rows={rows} onOpen={handleOpen} />
          </div>
        )}
      </div>

      <ApplicationDrawer
        id={selectedId}
        snapshot={selectedSnap}
        onClose={handleClose}
        onStatusChanged={handleStatusChanged}
        onOpenOther={handleOpen}
        onConvert={handleConvert}
        onOpenClient={handleOpenClientByPhone}
      />

      {/* key по заявке: модалка берёт предзаполнение из начального
          состояния, и для новой заявки её надо смонтировать заново */}
      {convertTarget !== null && (
        <CreateAccountModal
          key={convertTarget.id}
          open
          onOpenChange={handleConvertDone}
          application={convertTarget}
          onCreated={handleCreated}
        />
      )}
      <Toast message={toast} />
    </>
  );
};
